import '../styles/v2/pages/md-switches.css';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import fetchWithTenant from '../utils/fetchWithTenant';
import DevicesTabBar from '../components/DevicesTabBar';
import {
  Network, RefreshCw, Play, Wifi, WifiOff, Zap, ZapOff,
  ChevronRight, Clock, AlertCircle, CheckCircle, Loader,
  Server, Activity, MoreVertical, Search, Filter
, Router, Radio, ArrowLeftRight, HelpCircle, Archive, RotateCcw } from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import switchesTutorial from '../components/tutorials/configs/switchesTutorial';
import { computeSyncStatusBadge } from './switchStatusBadge.js';
import { computeSyncAction } from './switchSyncAction.js';

const POLL_INTERVAL = 15000; // 15s auto-refresh

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function formatUptime(seconds) {
  if (!seconds) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function SyncStatusBadge({ pollMode, isOnline, status }) {
  const s = computeSyncStatusBadge({ pollMode, isOnline, pollStatus: status });
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 8px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      color: s.color, background: s.bg,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
      {s.label}
    </span>
  );
}

function PoEBar({ used, budget }) {
  if (!budget || budget === 0) return <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>No PoE</span>;
  const pct = Math.min((used / budget) * 100, 100);
  const color = pct > 85 ? '#ef4444' : pct > 65 ? '#f59e0b' : '#00d4aa';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-secondary)' }}>
        <span>{used?.toFixed(1) ?? 0}W used</span>
        <span>{budget}W budget</span>
      </div>
      <div style={{ height: 4, borderRadius: 4, background: 'rgba(255,255,255,0.08)' }}>
        <div className="sw-summary-grid" style={{
          height: '100%', borderRadius: 4,
          width: `${pct}%`,
          background: color,
          transition: 'width 0.4s ease',
        }} />
      </div>
    </div>
  );
}

function PortDots({ portCount, portsOnline }) {
  if (!portCount) return null;
  const online = portsOnline ?? 0;
  const dots = Math.min(portCount, 24); // render max 24 dots
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, maxWidth: 160 }}>
      {Array.from({ length: dots }, (_, i) => (
        <div key={i} style={{
          width: 8, height: 8, borderRadius: 2,
          background: i < online ? '#00d4aa' : 'rgba(255,255,255,0.1)',
          transition: 'background 0.3s',
        }} />
      ))}
      {portCount > 24 && (
        <span style={{ fontSize: 10, color: 'var(--text-tertiary)', alignSelf: 'center' }}>+{portCount - 24}</span>
      )}
    </div>
  );
}

function SwitchCard({ sw, onStartMonitoring, onForcePoll, onArchive, onUnarchive, onNavigate, actionLoading }) {
  const isLoading = actionLoading === sw.id;
  const connectionMode = sw.integration?.connectionMode ?? '—';
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  return (
    <div
      onClick={() => onNavigate(sw.id)}
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 12,
        padding: '20px 22px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        position: 'relative',
        overflow: menuOpen ? 'visible' : 'hidden',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'rgba(0,212,170,0.3)';
        e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
      }}
    >
      {/* Online pulse strip */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: sw.isOnline ? 'var(--accent-primary, #00d4aa)' : 'transparent',
        opacity: sw.isOnline ? 1 : 0,
        transition: 'opacity 0.3s',
      }} />

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="sw-summary-grid" style={{
            width: 40, height: 40, borderRadius: 8,
            background: sw.isOnline ? 'rgba(0,212,170,0.12)' : 'rgba(107,114,128,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {sw.isOnline
              ? <Network size={20} color="#00d4aa" />
              : <Network size={20} color="#6b7280" />}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#e5e7eb', marginBottom: 2 }}>
              {sw.hostname || sw.managementIp || 'Unknown Switch'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              {sw.managementIp}
              {/* A vendor with no MdManufacturer enum value is stored as
                  GENERIC_SNMP with its real name in manufacturerName, which
                  is the only place that name is visible (868m1yttp). */}
              {sw.manufacturer === 'GENERIC_SNMP' && sw.manufacturerName
                ? ' · ' + sw.manufacturerName
                : ''}
              {' · '}{sw.model || 'Unknown Model'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {sw.controllerId && (
            <Radio size={14} color="#60a5fa" title={`Identified by controller: ${sw.controller?.name || 'unknown'}`} />
          )}
          {sw.archivedAt ? (
            <span className="sw-mfg-badge" style={{ background: 'rgba(107,114,128,0.15)', color: '#9ca3af' }}>
              {sw.archiveReason || 'Archived'}
            </span>
          ) : (
            <SyncStatusBadge pollMode={sw.pollMode} isOnline={sw.isOnline} status={sw.pollStatus} />
          )}
          <ChevronRight size={16} color="#6b7280" />
        </div>
      </div>

      {/* Stats row */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12,
        marginBottom: 14, paddingTop: 14,
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Ports</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e5e7eb' }}>
            {sw.portCount ?? '—'}
            {sw.portsOnline != null && (
              <span style={{ fontSize: 11, color: '#00d4aa', fontWeight: 400, marginLeft: 4 }}>
                ({sw.portsOnline} up)
              </span>
            )}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Uptime</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e5e7eb' }}>{formatUptime(sw.uptime)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Mode</div>
          <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)' }}>
            {connectionMode === 'LOCAL_API' ? 'Local API' : connectionMode === 'INCONTROL2' ? 'InControl2' : connectionMode === 'SNMP' ? 'SNMP' : connectionMode}
          </div>
        </div>
      </div>

      {/* PoE bar */}
      {sw.poeSupported && (
        <div style={{ marginBottom: 14 }}>
          <PoEBar used={sw.poeUsedWatts} budget={sw.poeBudgetWatts} />
        </div>
      )}

      {/* Port dots */}
      {sw.portCount > 0 && (
        <div style={{ marginBottom: 14 }}>
          <PortDots portCount={sw.physicalPortCount ?? sw.portCount} portsOnline={sw.portsOnline} />
        </div>
      )}

      {/* Footer */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-tertiary)' }}>
          <Clock size={11} />
          <span>Polled {timeAgo(sw.lastPolledAt)}</span>
        </div>
        <div
          ref={menuRef}
          style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}
          onClick={e => e.stopPropagation()}
        >
          {sw.archivedAt ? (<>
            {sw.archiveReason === 'MERGED_DUPLICATE' && sw.mergedIntoId && (
              <button
                onClick={() => onNavigate(sw.mergedIntoId)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 10px', borderRadius: 6, border: 'none',
                  background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)',
                  fontSize: 12, fontWeight: 500, cursor: 'pointer',
                }}
              >
                View survivor <ChevronRight size={11} />
              </button>
            )}
            <button
              onClick={() => onUnarchive(sw.id)}
              disabled={isLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 10px', borderRadius: 6, border: 'none',
                background: 'rgba(0,212,170,0.15)', color: '#00d4aa',
                fontSize: 12, fontWeight: 500, cursor: isLoading ? 'default' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {isLoading ? <Loader size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <RotateCcw size={11} />}
              Restore
            </button>
          </>) : computeSyncAction({ pollMode: sw.pollMode, pollStatus: sw.pollStatus }) === 'controller' ? (
            // CONTROLLER-mode rows are synced from the controller, not
            // polled by an agent -- same onForcePoll handler as the plain
            // Force Poll case below, just a different label. The backend's
            // poll endpoint already branches CONTROLLER-vs-AGENT internally
            // (868m55n4x, matches MdSwitchDetail.jsx's own button).
            <button
              onClick={() => onForcePoll(sw.id)}
              disabled={isLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 10px', borderRadius: 6, border: 'none',
                background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)',
                fontSize: 12, fontWeight: 500, cursor: isLoading ? 'default' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {isLoading ? <Loader size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={11} />}
              Sync from controller
            </button>
          ) : computeSyncAction({ pollMode: sw.pollMode, pollStatus: sw.pollStatus }) === 'startMonitoring' ? (
            <button
              onClick={() => onStartMonitoring(sw.id)}
              disabled={isLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 10px', borderRadius: 6, border: 'none',
                background: 'rgba(0,212,170,0.15)', color: '#00d4aa',
                fontSize: 12, fontWeight: 500, cursor: isLoading ? 'default' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {isLoading ? <Loader size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={11} />}
              Start Monitoring
            </button>
          ) : (
            <button
              onClick={() => onForcePoll(sw.id)}
              disabled={isLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 10px', borderRadius: 6, border: 'none',
                background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)',
                fontSize: 12, fontWeight: 500, cursor: isLoading ? 'default' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {isLoading ? <Loader size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={11} />}
              Force Poll
            </button>
          )}

          {!sw.archivedAt && (<>
            <button
              onClick={() => setMenuOpen(o => !o)}
              title="More actions"
              aria-label="More actions"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '5px 6px', borderRadius: 6, border: 'none',
                background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              <MoreVertical size={14} />
            </button>

            {menuOpen && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 20,
                minWidth: 150, padding: 4, borderRadius: 8,
                background: '#1a2235', border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              }}>
                <button
                  onClick={() => { setMenuOpen(false); onArchive(sw.id); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '8px 10px', borderRadius: 6, border: 'none',
                    background: 'transparent', color: '#e5e7eb',
                    fontSize: 13, cursor: 'pointer', textAlign: 'left',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <Archive size={14} /> Archive
                </button>
              </div>
            )}
          </>)}
        </div>
      </div>
    </div>
  );
}

export default function MdSwitches() {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(switchesTutorial);

  const navigate = useNavigate();
  const [switches, setSwitches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('switches');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [lastRefresh, setLastRefresh] = useState(null);
  const timerRef = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchSwitches = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetchWithTenant('/api/md/switches?includeArchived=true', {});
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSwitches(data.switches || data || []);
      setLastRefresh(new Date());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSwitches();
    timerRef.current = setInterval(() => fetchSwitches(true), POLL_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [fetchSwitches]);

  const handleStartMonitoring = async (switchId) => {
    setActionLoading(switchId);
    try {
      const res = await fetchWithTenant('/api/md/switches/start-monitoring', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ switchIds: [switchId] }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast('Monitoring started — polling will begin shortly');
      await fetchSwitches(true);
    } catch (e) {
      showToast(`Failed to start monitoring: ${e.message}`, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleForcePoll = async (switchId) => {
    setActionLoading(switchId);
    try {
      const res = await fetchWithTenant(`/api/md/switches/${switchId}/poll`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast('Force poll triggered');
      setTimeout(() => fetchSwitches(true), 3000);
    } catch (e) {
      showToast(`Failed to force poll: ${e.message}`, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchive = async (switchId) => {
    if (!confirm('Archive this device? It will be hidden from the active list. It reappears automatically if rediscovered, or you can restore it from the archived view.')) return;
    // Optimistic: mark archived in place (not removed) so it moves straight
    // to the Archived tab instead of vanishing until the next poll.
    setSwitches(prev => prev.map(s => s.id === switchId
      ? { ...s, archivedAt: new Date().toISOString(), archiveReason: s.archiveReason || 'MANUAL' }
      : s));
    try {
      const res = await fetchWithTenant(`/api/md/switches/${switchId}/archive`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast('Device archived');
    } catch (e) {
      showToast(`Failed to archive: ${e.message}`, 'error');
      fetchSwitches(true); // resync on failure
    }
  };

  const handleUnarchive = async (switchId) => {
    setActionLoading(switchId);
    // Optimistic: clear archive fields so the card moves back to its live tab immediately.
    setSwitches(prev => prev.map(s => s.id === switchId ? { ...s, archivedAt: null, archiveReason: null } : s));
    try {
      const res = await fetchWithTenant(`/api/md/switches/${switchId}/unarchive`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast('Device restored');
      await fetchSwitches(true); // pick up the real (possibly restored) managementIp
    } catch (e) {
      showToast(`Failed to restore: ${e.message}`, 'error');
      fetchSwitches(true); // resync on failure
    } finally {
      setActionLoading(null);
    }
  };

  const TAB_CATEGORY = { routers: 'ROUTER', switches: 'SWITCH', wireless: 'ACCESS_POINT', unclassified: 'UNKNOWN' };
  const filtered = switches.filter(sw => {
    // Archived devices only ever show in the Archived tab, regardless of category.
    if (sw.archivedAt) return false;

    // Category filter based on active tab. Phase 1: the schema default is
    // now UNKNOWN (not SWITCH), so a missing category must not display as
    // a false-positive SWITCH.
    const deviceCat = (sw.category || 'UNKNOWN').toUpperCase();
    const tabCat = TAB_CATEGORY[activeTab];
    if (tabCat && deviceCat !== tabCat) return false;

    const q = search.toLowerCase();
    const matchSearch = !q || (sw.hostname || '').toLowerCase().includes(q)
      || (sw.managementIp || '').includes(q)
      || (sw.model || '').toLowerCase().includes(q);
    const matchStatus = filterStatus === 'all'
      || (filterStatus === 'online' && sw.isOnline)
      || (filterStatus === 'offline' && !sw.isOnline)
      || (filterStatus === 'unmonitored' && sw.pollStatus === 'NEVER_SYNCED');
    return matchSearch && matchStatus;
  });

  // Archived tab: its own list, independent of the online/offline/unmonitored
  // status filter above (archival status, not live poll status, is what
  // matters here). Search still applies.
  const archivedDevices = switches.filter(s => !!s.archivedAt);
  const archivedFiltered = archivedDevices.filter(sw => {
    const q = search.toLowerCase();
    return !q || (sw.hostname || '').toLowerCase().includes(q)
      || (sw.managementIp || '').includes(q)
      || (sw.model || '').toLowerCase().includes(q);
  });

  const tabDevices = switches.filter(s => {
    if (s.archivedAt) return false;
    const tabCat = TAB_CATEGORY[activeTab];
    return !tabCat || (s.category || 'UNKNOWN').toUpperCase() === tabCat;
  });
  const onlineCount = tabDevices.filter(s => s.isOnline).length;
  const monitoredCount = tabDevices.filter(s => s.pollStatus !== 'NEVER_SYNCED').length;

  return (
    <div className="page-content">
      {/* Toast */}
      {toast && (
        <div className="sw-summary-grid" style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          padding: '12px 18px', borderRadius: 8,
          background: toast.type === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(0,212,170,0.15)',
          border: `1px solid ${toast.type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(0,212,170,0.3)'}`,
          color: toast.type === 'error' ? '#ef4444' : '#00d4aa',
          fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 8,
          animation: 'fadeIn 0.2s ease',
        }}>
          {toast.type === 'error' ? <AlertCircle size={15} /> : <CheckCircle size={15} />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="page-header-with-actions">
        <div>
          <h1 className="page-title"><Network size={28} /> Devices</h1>
          <p className="page-subtitle">
            Managed network infrastructure · Auto-refreshes every 15s
            {lastRefresh && (
              <span style={{ marginLeft: 8 }}>· Last updated {timeAgo(lastRefresh)}</span>
            )}
          </p>
        </div>
        <button
          onClick={() => fetchSwitches()}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 8, border: 'none',
            background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)',
            fontSize: 13, cursor: 'pointer', transition: 'all 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
        >
          <RefreshCw size={14} />
          Refresh
        </button>
        <button className="tutorial-help-btn" onClick={() => startTutorial(switchesTutorial.module, switchesTutorial.steps)} title="Take a tour" aria-label="Take a tour" style={{ marginLeft: 8 }}>
          <HelpCircle size={16} />
        </button>
      </div>

      <DevicesTabBar />
      {/* Summary Stats */}
      {tabDevices.length > 0 && (
        <div className="sw-summary-grid" style={{

        }}>
          {[
            { label: 'Total', value: tabDevices.length, icon: <Server size={16} color="#6b7280" /> },
            { label: 'Online', value: onlineCount, icon: <Wifi size={16} color="#00d4aa" />, accent: '#00d4aa' },
            { label: 'Offline', value: tabDevices.length - onlineCount, icon: <WifiOff size={16} color="#ef4444" />, accent: switches.length - onlineCount > 0 ? '#ef4444' : undefined },
            { label: 'Monitored', value: monitoredCount, icon: <Activity size={16} color="#60a5fa" />, accent: '#60a5fa' },
          ].map(stat => (
            <div key={stat.label} style={{
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 10, padding: '14px 18px',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              {stat.icon}
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: stat.accent || '#e5e7eb' }}>{stat.value}</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 1 }}>{stat.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="network-tabs" style={{
        display: 'flex', gap: 0, marginBottom: 20,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        {[
          { key: 'routers', label: 'Routers', icon: <Router size={15} />, category: 'ROUTER' },
          { key: 'switches', label: 'Switches', icon: <Network size={15} />, category: 'SWITCH' },
          { key: 'wireless', label: 'Wireless', icon: <Radio size={15} />, category: 'ACCESS_POINT' },
          { key: 'p2p', label: 'Point to Point', icon: <ArrowLeftRight size={15} />, category: null },
          { key: 'unclassified', label: 'Unclassified', icon: <HelpCircle size={15} />, category: 'UNKNOWN' },
          { key: 'archived', label: 'Archived', icon: <Archive size={15} />, category: null },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '10px 18px',
              background: 'none', border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid #00d4aa' : '2px solid transparent',
              color: activeTab === tab.key ? '#00d4aa' : '#6b7280',
              fontSize: 13, fontWeight: 500, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { if (activeTab !== tab.key) e.currentTarget.style.color = '#9ca3af'; }}
            onMouseLeave={e => { if (activeTab !== tab.key) e.currentTarget.style.color = '#6b7280'; }}
          >
            {tab.icon}
            {tab.label}
            {(tab.key === 'archived' || tab.category) && (() => {
              const count = tab.key === 'archived'
                ? archivedDevices.length
                : switches.filter(s => !s.archivedAt && (s.category || 'UNKNOWN') === tab.category).length;
              return count > 0 ? (
                <span style={{
                  background: activeTab === tab.key ? 'rgba(0,212,170,0.2)' : 'rgba(255,255,255,0.08)',
                  padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                  color: activeTab === tab.key ? '#00d4aa' : '#6b7280',
                  marginLeft: 2,
                }}>{count}</span>
              ) : null;
            })()}
          </button>
        ))}
      </div>

      {['routers', 'switches', 'wireless', 'unclassified'].includes(activeTab) && (<>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="sw-summary-grid" style={{
          flex: 1, minWidth: 200,
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 8, padding: '8px 12px',
        }}>
          <Search size={14} color="#6b7280" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by hostname, IP, model..."
            style={{
              border: 'none', outline: 'none', background: 'transparent',
              color: '#e5e7eb', fontSize: 13, width: '100%',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', 'online', 'offline', 'unmonitored'].map(f => (
            <button
              key={f}
              onClick={() => setFilterStatus(f)}
              style={{
                padding: '8px 14px', borderRadius: 8, border: 'none',
                background: filterStatus === f ? 'rgba(0,212,170,0.15)' : 'rgba(255,255,255,0.04)',
                color: filterStatus === f ? '#00d4aa' : '#6b7280',
                fontSize: 12, fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize',
                transition: 'all 0.15s',
              }}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, gap: 10, color: 'var(--text-tertiary)' }}>
          <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
          Loading switches...
        </div>
      ) : error ? (
        <div className="sw-summary-grid" style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '20px',
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 10, color: '#ef4444', fontSize: 13,
        }}>
          <AlertCircle size={16} />
          Failed to load switches: {error}
        </div>
      ) : filtered.length === 0 ? (
        <div className="sw-summary-grid" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: 200, gap: 12, color: 'var(--text-tertiary)',
        }}>
          <Network size={40} strokeWidth={1} color="#374151" />
          <div style={{ fontSize: 14 }}>
            {tabDevices.length === 0
              ? `No ${activeTab === 'routers' ? 'routers' : activeTab === 'wireless' ? 'wireless devices' : activeTab === 'unclassified' ? 'unclassified devices' : 'switches'} discovered yet. Run a network scan to detect devices.`
              : 'No devices match your filters.'}
          </div>
        </div>
      ) : (
        <div className="sw-summary-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: 16,
        }}>
          {filtered.map(sw => (
            <SwitchCard
              key={sw.id}
              sw={sw}
              onStartMonitoring={handleStartMonitoring}
              onForcePoll={handleForcePoll}
              onArchive={handleArchive}
              onNavigate={(id) => navigate(`/monitoring/network/${id}`)}
              actionLoading={actionLoading}
            />
          ))}
        </div>
      )}
      </>)}

      {activeTab === 'p2p' && (
        <div className="sw-summary-grid" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: 280, gap: 16, color: 'var(--text-tertiary)',
        }}>
          {activeTab === 'routers' && <Router size={48} strokeWidth={1} color="#374151" />}
          {activeTab === 'wireless' && <Radio size={48} strokeWidth={1} color="#374151" />}
          {activeTab === 'p2p' && <ArrowLeftRight size={48} strokeWidth={1} color="#374151" />}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 15, color: 'var(--text-secondary)', marginBottom: 4 }}>
              No {activeTab === 'routers' ? 'routers' : activeTab === 'wireless' ? 'wireless devices' : 'point-to-point links'} discovered yet
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              {activeTab === 'routers'
                ? 'Routers will appear here when discovered via SNMP or manual addition.'
                : activeTab === 'wireless'
                ? 'Wireless access points will appear here when discovered via SNMP.'
                : 'Point-to-point links will appear here when configured.'}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'archived' && (<>
      {/* Search only -- online/offline/unmonitored status is not meaningful for an archived device */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="sw-summary-grid" style={{
          flex: 1, minWidth: 200,
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 8, padding: '8px 12px',
        }}>
          <Search size={14} color="#6b7280" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by hostname, IP, model..."
            style={{
              border: 'none', outline: 'none', background: 'transparent',
              color: '#e5e7eb', fontSize: 13, width: '100%',
            }}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, gap: 10, color: 'var(--text-tertiary)' }}>
          <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
          Loading switches...
        </div>
      ) : error ? (
        <div className="sw-summary-grid" style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '20px',
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 10, color: '#ef4444', fontSize: 13,
        }}>
          <AlertCircle size={16} />
          Failed to load switches: {error}
        </div>
      ) : archivedFiltered.length === 0 ? (
        <div className="sw-summary-grid" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: 200, gap: 12, color: 'var(--text-tertiary)',
        }}>
          <Archive size={40} strokeWidth={1} color="#374151" />
          <div style={{ fontSize: 14 }}>
            {archivedDevices.length === 0
              ? 'No archived devices. Devices archived manually, merged as duplicates, or found orphaned appear here.'
              : 'No archived devices match your search.'}
          </div>
        </div>
      ) : (
        <div className="sw-summary-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: 16,
        }}>
          {archivedFiltered.map(sw => (
            <SwitchCard
              key={sw.id}
              sw={sw}
              onUnarchive={handleUnarchive}
              onNavigate={(id) => navigate(`/monitoring/network/${id}`)}
              actionLoading={actionLoading}
            />
          ))}
        </div>
      )}
      </>)}

            <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {showPrompt && (
        <TutorialPrompt
          module={switchesTutorial.module}
          moduleName={switchesTutorial.moduleName}
          steps={switchesTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
}
